/**
 * UnifiedAgreementContent — the merged Terms-of-Use + Privacy-Policy text
 * rendered as a single scrollable block inside the signup consent screen.
 *
 * Why one component for two documents?
 *   The user-facing consent flow treats Terms + Privacy as ONE inseparable
 *   agreement (you cannot accept one without the other). Splitting the UI
 *   into two checkboxes was confusing for end-users, so we merged the two
 *   documents into a single scrollable text and a single checkbox. The
 *   existing /terms and /privacy routes still serve the separate, fully
 *   formatted versions for users who arrive there from the footer.
 */

function H({ children, testid }) {
  return (
    <h2
      data-testid={testid}
      className="text-[15px] font-extrabold text-[#0A192F] mt-4 mb-1.5"
    >
      {children}
    </h2>
  );
}

function P({ children }) {
  return (
    <p className="text-[12.5px] text-[#3F4750] leading-[1.85] mb-2">
      {children}
    </p>
  );
}

function UL({ items }) {
  return (
    <ul className="list-disc pr-5 space-y-1 mb-2">
      {items.map((t, i) => (
        <li key={i} className="text-[12px] text-[#3F4750] leading-[1.75]">
          {t}
        </li>
      ))}
    </ul>
  );
}

function Note({ tone = "info", children }) {
  const tones = {
    info: "bg-[#EFF6FF] border-[#1D4ED8]/30 text-[#1E3A8A]",
    warn: "bg-[#FFF8E1] border-[#D4AF37]/40 text-[#7A5A00]",
    danger: "bg-[#FEF2F2] border-[#DC2626]/30 text-[#991B1B]",
  };
  return (
    <div
      className={`text-[12px] leading-[1.7] border rounded-xl p-2.5 mb-2 ${tones[tone] || tones.info}`}
    >
      {children}
    </div>
  );
}

export default function UnifiedAgreementContent() {
  return (
    <div className="text-right" data-testid="unified-agreement-content">
      <div className="text-[11px] text-[#5A6470] mb-1">
        آخر تحديث: 18 فبراير 2026 — الإصدار 2.0
      </div>
      <h1 className="text-[18px] font-extrabold text-[#0A192F] mb-1">
        اتّفاقيّة الاستخدام وَالخُصوصيّة لِتَطبيق LexIQ
      </h1>
      <Note tone="info">
        <b>إجمال</b>: هذه الاِتّفاقيّة تَجمَع بَين <b>شُروط الاِستِخدام</b> وَ
        <b> سِياسة الخُصوصيّة</b> في وَثيقة واحِدة. باِستِخدامك LexIQ فأنتَ
        تُقِرّ بأنّك قَرَأتَها بِالكامِل وَوافَقتَ عَلَيها كَوَحدة قانونيّة
        واحِدة لا تُجَزَّأ.
      </Note>

      {/* ===== PART A — TERMS OF USE ===== */}
      <H testid="agreement-part-terms">القسم الأوّل — شروط الاستخدام</H>

      <H>١. قبول الشروط</H>
      <P>
        هذا التطبيق («LexIQ»، «نحن»، «الخدمة») تطبيقٌ مُتاح عَبر المتصفّح
        وَنُسختي iOS وَAndroid. باستخدامك للتطبيق فأنت تُبرم اتّفاقيّة قانونيّة
        ملزِمة معنا.
      </P>
      <P>
        إن لم توافق على أيّ بند من هذه الشروط، يُرجى التوقّف عن استخدام التطبيق
        فوراً وحذف أيّ نسخة منه من أجهزتك.
      </P>

      <H>٢. أهلية الاستخدام</H>
      <UL items={[
        "يجب أن تكون قد بلغت سنّ ١٨ عاماً فأكثر.",
        "إن سجّلت بصفة «محامٍ»، يجب أن تكون مُسجَّلاً فعليّاً في نقابة المحامين العراقيّين بهويّة سارية المفعول.",
        "أنت مسؤول عن تقديم معلومات صحيحة ودقيقة عند التسجيل، وعن تحديثها فور تغيّرها.",
        "نحتفظ بالحقّ في التحقّق من هويّتك في أيّ وقت ورفض أيّ حساب لا يستوفي الشروط.",
      ]} />

      <H>٣. طبيعة الخدمة وحدودها</H>
      <P>
        LexIQ هو <b>أداة مساعد قانوني ذكيّة</b> تَعتَمِد على نَماذج لُغة مُتقدِّمة
        (Large Language Models) لِتَحليل المستندات، صياغة المسوّدات، البحث في
        قَرارات محكمة التمييز، وتنظيم الجَدولة وَالقضايا.
      </P>
      <Note tone="warn">
        <b>تنبيه قانوني جوهري</b>: مخرجات الذكاء الاصطناعي في LexIQ
        <u> ليست استشارة قانونيّة مُلزِمة</u>، ولا تُنشئ علاقة محامٍ/موكِّل بينك
        وبين الشركة المشغّلة. كلّ نتيجة أو صياغة أو رأي مُولَّد آلياً يجب أن
        يُراجَع من قِبَل محامٍ بشري قبل اعتماده في أيّ إجراء قضائي.
      </Note>
      <P><b>الخدمة مخصَّصة للأغراض التالية:</b></P>
      <UL items={[
        "مساعدة المحامين في إعداد القضايا والمسوّدات الأوّليّة.",
        "تسهيل البحث في قواعد القرارات والاجتهادات القضائيّة.",
        "تنظيم العلاقة بين المحامي والموكّل عبر بوّابة الموكّل (Client Portal).",
        "تقديم معلومات قانونيّة عامّة للزائرين بصورة تثقيفيّة.",
      ]} />

      <H>٤. الحسابات وكلمات المرور</H>
      <UL items={[
        "أنت المسؤول الوحيد عن سرّيّة كلمة مرورك وعن جميع النشاطات التي تتمّ من حسابك.",
        "يجب إخطارنا فوراً عند الاشتباه بأيّ وصول غير مصرَّح به.",
        "لا يجوز مشاركة الحساب مع أيّ شخص آخر أو تأجيره أو بيعه.",
        "نحتفظ بحقّ تعليق أو حذف أيّ حساب يخالف هذه الشروط دون إشعار مسبق.",
      ]} />

      <H>٥. الاستخدامات المحظورة</H>
      <P>يُحظر عليك كلّيّاً استخدام التطبيق لـ:</P>
      <UL items={[
        "أيّ نشاط مخالف للقوانين المعمول بها في العراق.",
        "انتحال شخصيّة محامٍ آخر أو تقديم معلومات هويّة مزوَّرة.",
        "محاولة اختراق أو هندسة عكسيّة أو نسخ كود التطبيق أو خوارزميّاته.",
        "إغراق الخدمة بطلبات آليّة (Scraping/Botting) أو محاولة تجاوز حدود الاستخدام.",
        "تحميل محتوى يحتوي على فيروسات أو برمجيّات خبيثة.",
        "استخدام التطبيق لإنشاء محتوى مُهين أو تشهيري أو يحرّض على العنف أو الكراهيّة.",
        "خرق سرّيّة المهنة (محامي/موكّل) أو مشاركة بيانات موكّليك بصورة غير قانونيّة.",
        "إعادة بيع مخرجات التطبيق على أنّها استشارة قانونيّة معتمَدة.",
      ]} />

      <H>٦. الملكيّة الفكريّة</H>
      <UL items={[
        "كلّ ما يتعلّق بالتطبيق — التصميم، الكود، الشعار، اسم «LexIQ» وشعاره — هو ملكيّة حصريّة للشركة المُشغِّلة ومحميّ بقوانين حقوق التأليف الدوليّة.",
        "تحتفظ أنت بالملكيّة الكاملة للمحتوى الذي تُنشئه بنفسك (قضاياك، مذكّراتك، مستنداتك).",
        "بمجرّد رفعك أيّ محتوى، تمنحنا ترخيصاً غير حصري لاستخدامه فقط لأغراض تشغيل الخدمة لصالحك.",
        "قرارات محكمة التمييز في «المكتبة» مأخوذة من مصادر عامّة، وتُقدَّم لأغراض البحث القانوني والتعليم.",
      ]} />

      <H>٧. مخرجات الذكاء الاصطناعي</H>
      <UL items={[
        "قد يحتوي المُخرَج على أخطاء، عدم دقّة، أو معلومات قديمة.",
        "أنت المسؤول عن مراجعة كلّ مُخرَج والتحقّق منه قبل أيّ اعتماد قانوني.",
        "لا نضمن أنّ المُخرَج سيُؤدّي إلى نتيجة قضائيّة معيّنة.",
        "الاستشهاد بقرار تمييز يجب التأكّد من رقمه وتاريخه من المصدر الرسمي قبل الاحتجاج به أمام المحكمة.",
      ]} />

      <H>٨. حدود المسؤوليّة</H>
      <Note tone="danger">
        <b>إخلاء مسؤوليّة هام</b>: في أقصى الحدود التي يسمح بها القانون، لا
        تتحمّل الشركة المُشغّلة لـ LexIQ أيّ مسؤوليّة عن أيّ أضرار مباشرة أو
        غير مباشرة أو تبعيّة تنشأ عن استخدامك للتطبيق، بما في ذلك على سبيل
        المثال لا الحصر: خسائر ماليّة، فقدان قضيّة، فقدان بيانات، تأخير مواعيد،
        أو أيّ خسارة معنويّة أو ماديّة أخرى.
      </Note>
      <P>
        الحدّ الأقصى لمسؤوليّتنا الإجماليّة تجاهك — بأيّ سبب كان — لا يتجاوز
        إجمالي المبالغ التي دفعتها فعليّاً للخدمة خلال الاثني عشر شهراً السابقة
        لتاريخ المطالبة. خدمة LexIQ تُقدَّم «كما هي» (As-Is) و«حسب توفّرها»
        (As-Available) دون أيّ ضمانات صريحة أو ضمنيّة من أيّ نوع.
      </P>

      <H>٩. الاشتراك والدفع</H>
      <UL items={[
        "بعض ميزات التطبيق متاحة مجّاناً، وبعضها يتطلّب اشتراكاً مدفوعاً.",
        "تفاصيل الباقات والأسعار معروضة داخل التطبيق وقد تتغيّر بإشعار مسبق ١٤ يوماً.",
        "تَتِمّ المَدفوعات عَبر بَوّابات دَفع مُؤَمَّنة مَحَلّيّاً وَدَوليّاً. لا نَحتَفِظ بِأَرقام بِطاقات الدَّفع على خَوادمنا.",
        "جميع المدفوعات نهائيّة وغير قابلة للاسترداد، إلّا في حالات خطأ تقني مُثبَت أو خرق صريح من جانبنا، وفقاً لما يقرّره فريق الدعم.",
        "تجديد الاشتراك تلقائي ما لم تُلغِه من إعدادات حسابك قبل ٢٤ ساعة من تاريخ التجديد.",
      ]} />

      <H>١٠. التعليق أو الإنهاء</H>
      <UL items={[
        "يحقّ لنا تعليق أو إنهاء حسابك دون إشعار مسبق إن خالفت أيّ بند من هذه الشروط.",
        "يحقّ لك إنهاء استخدامك للتطبيق في أيّ وقت عبر «حذف الحساب» من إعدادات حسابك.",
        "بعد الإنهاء، نحتفظ بالحقّ في حذف كافّة بياناتك خلال ٧ أيّام عمل، باستثناء ما يُطلب الاحتفاظ به قانوناً.",
      ]} />

      <H>١١. التعديلات على الشروط</H>
      <P>
        قد نُعدّل هذه الشروط من وقتٍ لآخر لتلبية متطلّبات قانونيّة أو تقنيّة
        جديدة. سنُعلِمك بأيّ تعديل جوهري قبل ١٤ يوماً من سريانه عبر البريد
        الإلكتروني أو إشعار داخل التطبيق. استمرارك في استخدام الخدمة بعد تاريخ
        السريان يُعدّ قبولاً ضمنيّاً للنسخة الجديدة من الشروط.
      </P>

      <H>١٢. القانون الواجب التطبيق والاختصاص</H>
      <P>
        تخضع هذه الشروط لِأحكام القانون العراقيّ ذات الصِّلة. يحقّ لكلا الطرفَين،
        عند نشوء أيّ نزاع، اللجوء إلى الوساطة الودّيّة خلال ٣٠ يوماً قبل اللجوء
        إلى القضاء.
      </P>

      <H>١٣. شروط متاجر التطبيقات</H>
      <UL items={[
        "Apple يُعتبر مستفيداً من الطرف الثالث (Third-Party Beneficiary) في هذه الشروط، وله حقّ تنفيذها بحقّك.",
        "يحقّ لك طلب استرداد عبر Apple أو Google ضمن سياسات المتجر الرسميّة.",
        "أنت مسؤول عن الالتزام بشروط متجر التطبيقات الذي حمَّلت منه LexIQ.",
      ]} />

      {/* ===== PART B — PRIVACY POLICY ===== */}
      <H testid="agreement-part-privacy">القسم الثاني — سياسة الخصوصيّة</H>

      <Note tone="info">
        <b>إجمال</b>: نَحتَرِم خُصوصيّتك. نَجمَع الحَدّ الأَدنى مِنَ البيانات
        اللازِمة لِتَشغيل الخدمة، وَلا نَبيع بياناتك لأَيّ طَرَف ثالث،
        وَنَمنَحُك حَقّ الوُصول وَالحَذف وَالتَّصدير في أيّ وَقت.
      </Note>

      <H>١. البيانات التي نجمعها</H>
      <P><b>أ) بيانات الحساب الأساسيّة</b></P>
      <UL items={[
        "الاسم الكامل (المحامي: مستخرَج من بطاقة النقابة — العميل: يُدخله يدوياً).",
        "البريد الإلكتروني (يُستخدم للتسجيل، الإشعارات، استعادة كلمة المرور).",
        "كلمة المرور (لا تُخزَّن نصّياً — نخزّن hash مشفَّر بـ bcrypt فقط).",
        "رقم الهاتف (اختياري — يُستخدم لتواصل العملاء مع المحامي).",
      ]} />
      <P><b>ب) بيانات المحامين خصّيصاً</b></P>
      <UL items={[
        "صورة هويّة نقابة المحامين العراقيين (تُحلَّل عبر نموذج لُغوي مُتقدِّم لاستخراج الاسم، رقم النقابة، الدرجة، تاريخ الانتهاء).",
        "صورة الهويّة تُخزَّن في بِنية تخزين سَحابيّة آمنة لمدّة 30 يوماً للتحقّق، ثمّ تُحذَف تلقائياً.",
        "الموقع التقريبي (المحافظة والقَضاء) — يُملأ من الطلب فقط إن منحت الإذن، ولا نتتبّع تحرّكاتك.",
      ]} />
      <P><b>ج) محتوى التطبيق الذي تُنشئه</b></P>
      <UL items={[
        "قضاياك، عملاؤك، أحداث الجدول، الملاحظات، المستندات المرفوعة.",
        "المحادثات مع المساعد القانوني الذكي.",
        "كلّ هذا المحتوى معزول لحسابك تماماً — لا يطّلع عليه محامون آخرون أبداً.",
      ]} />
      <P><b>د) بيانات تقنيّة تلقائيّة</b></P>
      <UL items={[
        "عنوان IP، نوع الجهاز، نسخة المتصفّح أو نظام التشغيل.",
        "سجلّات الاستخدام (متى دخلت، أيّ صفحات زرتها) — لمدّة 90 يوماً.",
        "Push Notification Tokens (لإرسال إشعارات).",
      ]} />
      <P><b>هـ) ما لا نجمعه</b></P>
      <UL items={[
        "لا نجمع موقعك الجغرافي المستمرّ.",
        "لا نقرأ جهات اتّصالك أو رسائلك.",
        "لا نبيع بياناتك لشركات إعلانيّة أو أيّ طرف ثالث.",
        "لا نستخدم Cookies تتبّع إعلاني (Tracking Pixels).",
      ]} />

      <H>٢. كيف نستخدم بياناتك</H>
      <UL items={[
        "تشغيل الخدمة الأساسيّة (إنشاء حساب، تسجيل دخول، حفظ قضاياك).",
        "التحقّق من هويّة المحامي عبر بطاقة النقابة (مرّة واحدة فقط).",
        "إرسال إشعارات تتعلّق بحسابك (مواعيد، تنبيهات، تحديثات).",
        "تحسين دقّة المساعد الذكي (نُحلّل أسئلتك إجمالاً — لا نحتفظ بهويّتك).",
        "الامتثال للقوانين السارية في العراق.",
        "كشف ومنع الاحتيال أو سوء الاستخدام.",
      ]} />
      <Note tone="warn">
        <b>تنبيه مهنّي مهم</b>: نتعامل مع كلّ محتواك القانوني بسرّيّة تامّة
        تطابق متطلّبات سرّيّة المهنة (المحامي/الموكِّل). لن يطّلع موظّفونا على
        محتوى قضاياك إلاّ في حالة طلب قضائي رسمي ملزم.
      </Note>

      <H>٣. مزوّدو الخدمات (الأطراف الثالثة)</H>
      <P>
        لِتَشغيل LexIQ نَستَعين بِمزوِّدي خدمات تِقنيّة دَوليّين مُعتَمَدين،
        يَلتَزِمون بِأَعلى معايير حماية البيانات (SOC 2, ISO 27001, GDPR)
        وَيَرتبِطون بنا باتّفاقيّات صارمة لِمعالجة البيانات (DPA).
      </P>
      <P><b>الأطراف الثالثة التي نُشارِك مَعَها بَعض البيانات (بشكل صريح وشفّاف):</b></P>
      <UL items={[
        "Anthropic (Claude Sonnet 4.5) — مقرّها الولايات المتحدة. تُرسَل إليها: (أ) نُصوص أَسئِلَتِك في المُحادَثة القانونيّة، (ب) نُصوص المُستَنَدات التي تُريد تَحليلها، (ج) صُوَر المُستَنَدات في خاصّيّة OCR. الغَرَض: تَوليد الرَّدّ القانوني وَاستِخراج النَّصّ. لا تَحتَفِظ Anthropic بِبَياناتك لِتَدريب نَماذِجها (بِناءً على DPA مَوقَّعَة).",
        "OpenAI (Whisper API) — مقرّها الولايات المتحدة. تُرسَل إليها: تَسجيلات الصَّوت التي تُريد تَحويلها إلى نَصّ. الغَرَض: نَسخ الصَّوت (Speech-to-Text). لا تُستَخدَم لِتَدريب نَماذِج.",
        "Stripe, Inc. — مقرّها الولايات المتحدة. تُرسَل إليها: بَيانات الدَّفع (رَقم البِطاقة، تاريخ الانتِهاء، CVV، اسم حامِل البِطاقة، عُنوان الفَوترة). الغَرَض: مُعالَجَة الاِشتِراكات. LexIQ لا تَرى وَلا تُخَزِّن بَيانات بِطاقَتِك أَبداً.",
        "Amazon Web Services (AWS S3, SES) — مقرّها الولايات المتحدة. تُرسَل إليها: (أ) الملفّات وَالمُستَنَدات التي ترفعها (مُشَفَّرَة AES-256)، (ب) رَسائل البريد الإلكتروني الصادرة (تَحقّق، إشعارات). الغَرَض: تَخزين آمِن وَإرسال بَريد.",
        "MongoDB Atlas — مقرّها الولايات المتحدة. الغَرَض: تَخزين قاعدة البَيانات الرَّئيسيّة (حِسابات المُستَخدِمين، قَضايا، مُحادَثات). كُلّ البيانات مُشَفَّرة في الراحة (Encryption at Rest) وَعِند النَّقل (TLS 1.3).",
        "Agora.io — مقرّها الولايات المتحدة/الصين. تُرسَل إليها: بَيانات المُكالَمات الصَّوتيّة المُشَفَّرة End-to-End فَقَط. لا تُسَجَّل المُكالَمات إلاّ إذا فَعَّلت أَنت ذٰلِك يَدَوياً.",
        "ZenRows — مقرّها الاتحاد الأوروبي. الغَرَض: جَلب البَيانات القانونيّة العامّة مِن مَواقِع المَحاكِم الرَّسميّة (لا تَشمَل أَي بَيانات مُستَخدِمين).",
        "Apple Push Notification Service (APNs) — مقرّها الولايات المتحدة. الغَرَض: تَوصيل الإشعارات لِجهاز iPhone الخاصّ بِك.",
      ]} />
      <P>
        <b>مُوافَقَتُك:</b> بِاستِخدامِك لِخاصّيّات LexIQ الذَّكيّة (المُحادَثة، OCR،
        تَحويل الصَّوت، تَحليل المُستَنَدات)، فأنت تُوافِق صَراحَةً على إرسال المُدخَلات
        اللازِمة لهٰذِه الخِدمات إلى الأَطراف الثالِثة المَذكورة أَعلاه لِأَغراض المُعالَجة
        فَقَط. تَستَطيع في أَيّ وَقت تَجَنُّب استِخدام هٰذِه الخاصّيّات وَالاستِفادَة مِن
        الميزات الأُخرى (تَصَفُّح القَوانين، تَنظيم القَضايا).
      </P>
      <P>
        <b>حِمايَة إضافيّة:</b> جَميع هٰؤُلاء المُزَوِّدين وَقَّعوا مَعَنا اِتّفاقيّات مُعالَجَة
        بَيانات (DPA) تُلزِمُهُم بِنَفس مُستَوى حِمايَتِنا لِبَياناتك، وَتَمنَعُهُم مِن
        استِخدام بَياناتك لِأَغراض أُخرى كَالتَّسويق أَو التَّدريب أَو البَيع.
      </P>
      <H>٤. أين تُخزَّن بياناتك ومدّة الاحتفاظ</H>
      <UL items={[
        "قاعدة البيانات الرَّئيسيّة: مَخزَن بيانات سَحابيّ مُعتَمَد دَوليّاً (مُشَفَّر في الرَّاحة وَعند النَّقل).",
        "بيانات الحساب: تبقى حتى تحذف حسابك.",
        "صور بطاقة النقابة: تُحذَف تلقائياً بعد 30 يوماً من التحقّق.",
        "سجلّات الاستخدام: 90 يوماً ثمّ تُمسَح.",
        "محادثات المساعد الذكي: 12 شهراً، يمكنك حذفها يدوياً في أيّ وقت.",
        "النسخ الاحتياطيّة المشفَّرة: تُحتفَظ بها 7 أيّام كحدّ أقصى ثمّ تُتلَف.",
      ]} />

      <H>٥. حقوقك</H>
      <P>تُكفل لك في أيّ وقت الحقوق التالية، تطبيقاً لمعايير GDPR وأفضل الممارسات الدوليّة:</P>
      <UL items={[
        "حقّ الاطّلاع: تستطيع تنزيل نسخة من جميع بياناتك من إعدادات الحساب.",
        "حقّ التصحيح: يمكنك تعديل أيّ بيان شخصي مباشرةً.",
        "حقّ المحو (Right to be Forgotten): زرّ «حذف الحساب» يمحو كلّ بياناتك خلال 7 أيّام.",
        "حقّ التقييد: يمكنك إيقاف الإشعارات أو تقييد المعالجة.",
        "حقّ النقل: تصدير بياناتك بصيغة JSON قياسيّة.",
        "حقّ الاعتراض: يمكنك الاعتراض على أيّ معالجة لا تعتبرها ضروريّة.",
      ]} />
      <P>
        لممارسة أيّ من هذه الحقوق، تواصل معنا على البريد المذكور أدناه. نلتزم
        بالردّ خلال 30 يوماً كحدّ أقصى.
      </P>

      <H>٦. الأمان</H>
      <UL items={[
        "تشفير TLS 1.3 لكلّ الاتّصالات بين تطبيقك وخوادمنا.",
        "تشفير AES-256 للبيانات في الراحة.",
        "كلمات المرور مُشفَّرة بـ bcrypt مع salt فردي لكلّ مستخدم.",
        "رموز OTP محدودة المحاولات (5) ومحدودة الوقت (10 دقائق).",
        "مراقبة محاولات الاختراق على مدار 24 ساعة.",
        "نسخ احتياطيّة يوميّة مشفّرة في مواقع جغرافيّة متعدّدة.",
      ]} />
      <P>
        عند اكتشاف أيّ خرق بيانات، نلتزم بإخطار المتضرّرين خلال 72 ساعة وفقاً
        لـ GDPR.
      </P>

      <H>٧. الأطفال</H>
      <P>
        تطبيق LexIQ موجَّه للمحترفين القانونيّين والبالغين فقط. لا نقبل
        مستخدمين دون <b>سنّ ١٨ عاماً</b>. إن اكتشفنا حساباً لقاصر، نحذفه فوراً.
      </P>

      <H>٨. تواصَل معنا</H>
      <UL items={[
        "الدعم الفنّي: support@lexiq.vip",
      ]} />
      <P>نلتزم بالردّ خلال ٧٢ ساعة عمل كحدّ أقصى.</P>

      <Note tone="info">
        <b>الإقرار النِّهائيّ</b>: بِتَأشيرك المُرَبَّع أَدناه وَالضَّغط على
        «أُوافِق وَأَتابِع» فأنتَ تُقِرّ بأنّك قَرَأتَ القِسمَين أعلاه
        (الشُّروط + الخُصوصيّة) بِالكامِل، فَهِمتَهُما، وَوافَقتَ عَلَيهِما
        كَوَحدة قانونيّة واحِدة لا تُجَزَّأ.
      </Note>
    </div>
  );
}
